import React from 'react';
import { setPageHeaderTextActionCreator, setAlertActionCreator } from 'plugins/store/reducers/common.reducer';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { withSnackbar } from 'notistack';
import {
	createAppointment,
	getAppointment,
	updateAppointment,
	deleteAppointment,
} from 'services/appointment.service';
import appointmentVacationDuration from 'utils/enums/appointment-vacation-duration.enum';
import appointmentVacationTypes from 'utils/enums/appointment-types.enum';
import { shortDateFormat } from 'resolvers/date-format.resolver';

import Tooltip from '@material-ui/core/Tooltip';
import withStyles from '@material-ui/core/styles/withStyles';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import styles from 'assets/jss/material-dashboard-pro-react/components/vacationFormStyle';

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Datetime from 'react-datetime';
import DateRangeIcon from '@material-ui/icons/DateRange';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import Button from 'components/CustomButtons/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import SnackMessage from 'components/Snackbar/SnackMessage';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import RouteLeavingGuard from 'components/RouteLeavingGuard/RouteLeavingGuard';
import layoutNames from 'utils/enums/layout-names.enum';

class VacationForm extends React.Component {
	state = {
		isChangedForm: false,
		loading: false,
		granularity: 'daily',
		dateFrom: new Date(),
		dateTo: null,
		timeFrom: moment().set({ hour: 13, minute: 0, second: 0, millisecond: 0 }).toDate(),
		timeTo: moment().set({ hour: 14, minute: 0, second: 0, millisecond: 0 }).toDate(),
		appointmentId: null,
		isOwnAppointment: false,
	};

	componentDidMount() {
		if (this.props.match.params.id) {
			this.getVacation();
		}
		this.props.setPageHeaderText('Setup Vacation');
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	getVacation = async () => {
		const appointmentId = this.props.match.params.id;
		this.setState({
			appointmentId,
			loading: true,
		});
		try {
			const appointment = await getAppointment(appointmentId);
			const isOwnAppointment = appointment.userId === this.props.userId;
			this.setState({ isOwnAppointment });
			if (!appointment) {
				this.setState({ isChangedForm: false }, () => {
					this.props.history.push(`/${layoutNames.portal}/scheduler`);
					this.showMessage('Vacation is not found');
				});
				return;
			}

			let endTimeAdjusted = new Date(appointment.endTime);
			endTimeAdjusted.setSeconds(endTimeAdjusted.getSeconds() + 1);

			if (appointment.vacationLastingType === appointmentVacationDuration.WHOLE_DAYS) {
				this.setState({
					granularity: 'daily',
					dateFrom: new Date(appointment.startTime),
					dateTo: endTimeAdjusted,
				});
			} else {
				this.setState({
					granularity: 'hourly',
					dateFrom: new Date(appointment.startTime),
					timeFrom: new Date(appointment.startTime),
					timeTo: endTimeAdjusted,
				});
			}
		} catch (error) {
			if (error.errorCode === 'GET_APPOINTMENT_NOT_ALLOWED') {
				const { history } = this.props;
				this.showMessage('The vacation belongs to another user');
				history.replace({ pathname: `/${layoutNames.portal}/scheduler` });
				return;
			}
			this.showMessage(error.resolvedErrorMessage);
		}
		this.setState({ loading: false });
	}

	handleGranularity = (granularity) => {
		this.setState({ granularity, dateTo: '' });
	}

	handleDatePicker = (name, value) => {
		this.setState({ [name]: value, isChangedForm: true }, () => {
			if (name === 'dateFrom') {
				const date = this.state.dateFrom.getDate();
				const month = this.state.dateFrom.getMonth();
				const year = this.state.dateFrom.getFullYear();
				const timeFrom =  moment(this.state.timeFrom).set({ year, month, date }).toDate();
				const timeTo = moment(this.state.timeTo).set({ year, month, date }).toDate();
				this.setState({ timeFrom, timeTo });
			}
		});
	}

	save = async () => {
		let startTime;
		let endTime;
		let vacationLastingType;
		if (this.state.granularity === 'daily') {
			vacationLastingType = appointmentVacationDuration.WHOLE_DAYS;
			if (!this.state.dateTo) {
				this.showMessage('You need to choose End date');
				return false;
			}
			startTime = moment(this.state.dateFrom).startOf('day').toDate();
			endTime = moment(this.state.dateTo).endOf('day').toDate();
		} else {
			vacationLastingType = appointmentVacationDuration.HOURS;

			const hourFrom = this.state.timeFrom.getHours();
			const minutesFrom = this.state.timeFrom.getMinutes();
			const hourTo = this.state.timeTo.getHours();
			const minutesTo = this.state.timeTo.getMinutes();

			startTime = moment(this.state.dateFrom).set({ hour: hourFrom, minute: minutesFrom, second: 0, millisecond: 0 }).toDate();
			endTime = moment(this.state.dateFrom).set({ hour: hourTo, minute: minutesTo, second: 0, millisecond: 0 }).toDate();

			if (startTime < new Date() || endTime < new Date()) {
				this.showMessage('You cannot set up vacation earlier than current date /time');
				return false;
			} else if (startTime >= endTime) {
				this.showMessage('Start Date should be earlier than End Date');
				return false;
			}
		}

		const data = {
			userId: this.props.userId,
			startTime,
			endTime,
			type: appointmentVacationTypes.VACATION,
			vacationLastingType,
			name: 'Vacation',
		};
		this.setState({ loading: true });
		try {
			if (this.state.appointmentId) {
				await updateAppointment(data, this.state.appointmentId);
			} else {
				await createAppointment(data);
			}
			this.setState({ isChangedForm: false }, () => {
				this.props.history.push(`/${layoutNames.portal}/scheduler`);
			});
		} catch (error) {
			this.showMessage(error.resolvedErrorMessage);
			this.setState({ loading: false });
		}
	}

	showMessage = (message = '', color = 'danger') => {
		const { enqueueSnackbar } = this.props;
		enqueueSnackbar(message, {
			content: (key, message) => <SnackMessage id={key} message={message} color={color} />,
		})
	}

	remove = () => {
		const { setAlert } = this.props;
		const warning = {
			type: 'warning',
			title: 'Do you want to remove current  Vacation?',
			message: 'This action can not be undone',
			confirmBtnText: 'Remove',
			cancelBtnText: 'Cancel',
			onConfirm: async () => {
				try {
					await deleteAppointment(this.state.appointmentId);
					setAlert(null);
					this.setState({ isChangedForm: false });
					this.props.history.push(`/${layoutNames.portal}/scheduler`);
				} catch (error) {
					this.showMessage(error.resolvedErrorMessage);
					setAlert(null);
				}
			},
			onCancel: () => {
				setAlert(null);
			}
		};
		setAlert(warning);
	}

	render() {
		const {
			classes,
			location,
		} = this.props;

		const isCreation = location.pathname === `/${layoutNames.portal}/scheduler/create-vacation`;
		const isAbleChanging = isCreation || this.state.isOwnAppointment;

		const startDayPicker = (<div>
			<span className={classes.datetimeLabel}>
				{this.state.granularity === 'daily' ? 'First Day' : 'Date'}
			</span>
			<div className={classes.datetimeWrapper}>
				<DateRangeIcon className={classes.datetimeIcon} />
				<Datetime
					isValidDate={({ _d }) => {
						if (_d < moment().subtract(1, 'day')) {
							return false;
						} else if (this.state.dateTo < _d && this.state.granularity === 'daily' && this.state.dateTo) {
							return false;
						}
						return true;
					}}
					inputProps={{
						readOnly: true,
						disabled: this.state.loading || !isAbleChanging
					}}
					timeFormat={false}
					dateFormat={shortDateFormat}
					onChange={({ _d }) => this.handleDatePicker('dateFrom', _d)}
					value={this.state.dateFrom}
				/>
			</div>
		</div>);

		const endDayPicker = (<div>
			<span className={classes.datetimeLabel}>
				{'Last Day'}
			</span>
			<div className={classes.datetimeWrapper}>
				<DateRangeIcon className={classes.datetimeIcon} />
				<Datetime
					isValidDate={({ _d }) => {
						if (_d < moment().subtract(1, 'day')) {
							return false;
						} else if (moment(this.state.dateFrom).startOf('day') > _d) {
							return false;
						}
						return true;
					}}
					inputProps={{
						readOnly: true,
						disabled: this.state.loading || !isAbleChanging
					}}
					timeFormat={false}
					dateFormat={shortDateFormat}
					onChange={({ _d }) => this.handleDatePicker('dateTo', _d)}
					value={this.state.dateTo}
				/>
			</div>
		</div>);

		const timeRangePicker = (
			<GridContainer alignItems="flex-end" justify="space-between">
				<GridItem xs={12} sm={6} md={6}>
					<div className={classes.datetimeWrapper}>
						<AccessTimeIcon className={classes.datetimeIcon} />
						<Datetime
							timeConstraints={{ minutes: { step: 15 } }}
							inputProps={{
								readOnly: true,
								disabled: this.state.loading || !isAbleChanging
							}}
							dateFormat={false}
							onChange={({ _d }) => this.handleDatePicker('timeFrom', _d)}
							value={this.state.timeFrom}
						/>
					</div>
				</GridItem>
				<GridItem xs={12} sm={6} md={6}>
					<div className={classes.datetimeWrapper}>
						<AccessTimeIcon className={classes.datetimeIcon} />
						<Datetime
							timeConstraints={{ minutes: { step: 15 } }}
							inputProps={{
								readOnly: true,
								disabled: this.state.loading || !isAbleChanging
							}}
							dateFormat={false}
							onChange={({ _d }) => this.handleDatePicker('timeTo', _d)}
							value={this.state.timeTo}
						/>
					</div>
				</GridItem>
			</GridContainer>
		);

		const loading = this.state.loading && <div className={classes.loadingIndicator}><LoadingIndicator /></div>

		const routeLeavingGuard = (
			<RouteLeavingGuard
				when={this.state.isChangedForm}
				navigate={path => this.props.history.push(path)}
				shouldBlockNavigation={() => {
					if (this.state.isChangedForm) {
						return true;
					}
					return false
				}}
			/>
		);

		return (
			<Card>
				{loading}
				{routeLeavingGuard}
				<CardBody>
					<GridItem>
						<GridContainer alignItems="center" justify="space-between">
							<div>
								<FormControlLabel
									disabled={this.state.loading || !isAbleChanging}
									control={
										<Radio
											checked={this.state.granularity === "daily"}
											onChange={() => this.handleGranularity('daily')}
											value="daily"
											classes={{
												checked: classes.radio,
												root: classes.radioRoot,
											}}
										/>
									}
									label="Daily"
								/>
								<FormControlLabel
									disabled={this.state.loading || !isAbleChanging}
									control={
										<Radio
											checked={this.state.granularity === "hourly"}
											onChange={() => this.handleGranularity('hourly')}
											value="hourly"
											classes={{
												checked: classes.radio,
												root: classes.radioRoot,
											}}
										/>
									}
									label="Hourly"
								/>
							</div>
							{
								!isCreation &&
								<Tooltip title="Remove Vacation">
									<Button
										color="transparent"
										simple
										aria-label="Remove"
										justIcon
										onClick={this.remove}
										disabled={this.state.loading}
									>
										<DeleteIcon />
									</Button>
								</Tooltip>
							}
						</GridContainer>
					</GridItem>
					<GridContainer alignItems="flex-end" justify="space-between">
						<GridItem xs={6}>
							{startDayPicker}
						</GridItem>
						<GridItem xs={6}>
							{
								this.state.granularity === 'daily'
									? endDayPicker
									: timeRangePicker
							}
						</GridItem>
					</GridContainer>
					<GridItem>
						<GridContainer style={{ marginTop: '10px' }} alignItems="center" justify="flex-end">
							<Button
								color="rose"
								className={classes.saveButton}
								onClick={this.save}
								disabled={this.state.loading || !isAbleChanging}
							>
								Save
								</Button>
						</GridContainer>
					</GridItem>
				</CardBody>
			</Card>
		);
	}
}

const mapStateToProps = (state) => ({
	userId: state.auth?.auth?.id,
});

const mapDispatchToProps = (dispatch) => ({
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch,
	),
	setAlert: bindActionCreators(
		setAlertActionCreator,
		dispatch,
	),
});

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(withSnackbar(VacationForm)));
